/**
 * Verizon Home Device Protect — the "View plan details" data set (openspec long-form-ai).
 *
 * This is the in-app replacement for the printed VHDP reference sheet experts hold during calls.
 *
 * SINGLE SOURCE OF TRUTH: the source PDF only.
 *   VDT-20260331-1_VHDP_11x17_Printable_Sheet_260331AP_V1.pdf  (V1.0)
 *
 * Every fact below is lifted from that sheet and carries a `source` string citing the exact block it
 * came from (General Information field, or Product Coverage category). Nothing here is sourced from the
 * demo/prototype — the demo was found to diverge from the sheet (it split the single "Gaming Systems &
 * Handheld Gaming Devices" line into two rows with different coverage, invented "popular examples" and a
 * "doesn't cover phones" line, and added "+ tax" to the per-claim service fees). Those divergences are
 * NOT reproduced here. If a fact is not on the sheet, it is not here. When the sheet is revised, bump
 * `meta.version` and re-derive.
 *
 * COVERAGE IS ENCODED AS THE SHEET ENCODES IT: a per-approved-claim `serviceFee` plus an `adh` flag —
 * the sheet's "(ADH)" marker, defined in its KEY as "Accidental Damage from Handling". Each row's
 * coverage descriptor ("Breakdowns only" / "Breakdowns + accidental damage") is DERIVED from that flag
 * by `coverageLabel`, not stored: breakdown coverage is the base these plans carry and ADH is the
 * sheet's documented add-on, so the descriptor is a rendering of the marker rather than a new fact.
 * Category counts are DERIVED from `devices`, never stored, so they cannot drift.
 */

const PDF = "VHDP printable sheet (VDT-20260331, V1.0)";
const GI = `${PDF} › General Information`;
const PC = `${PDF} › Product Coverage`;

/**
 * Render a whole-dollar per-claim service fee, e.g. "$99". No tax: the sheet's "plus applicable tax"
 * applies to the $25 monthly price, NOT to service fees ("$0, $49, $99 — Per approved claim").
 */
export const formatServiceFee = (fee: number): string => `$${fee}`;

/** The sheet KEY: "ADH – Accidental Damage from Handling". Shown on ADH rows; narrowed by qualifier. */
export const adhLabel = (device: Pick<VhdpDevice, "adhQualifier">): string =>
    device.adhQualifier
        ? `Includes accidental damage from handling (${device.adhQualifier})`
        : "Includes accidental damage from handling";

/**
 * The coverage descriptor next to each device row's fee. DERIVED from the sheet's `(ADH)` marker, not
 * stored: these plans cover mechanical/electrical breakdown as the base, and the sheet's ADH marker is
 * the documented add-on for accidental damage from handling. So no marker → "Breakdowns only"; marker →
 * "Breakdowns + accidental damage" (narrowed by the sheet's qualifier, e.g. printers' "screen only").
 */
export const coverageLabel = (device: Pick<VhdpDevice, "adh" | "adhQualifier">): string => {
    if (!device.adh) return "Breakdowns only";
    return device.adhQualifier
        ? `Breakdowns + accidental damage (${device.adhQualifier})`
        : "Breakdowns + accidental damage";
};

export interface VhdpCategory {
    /** Slug used as the chip value and the device-id prefix, e.g. "home-entertainment". */
    id: string;
    name: string;
    source: string;
}

export interface VhdpDevice {
    /** "<categoryId>:<slug>", e.g. "home-entertainment:tvs". */
    id: string;
    name: string;
    categoryId: string;
    /** Per-approved-claim service fee in whole dollars, verbatim from the sheet: 0, 49, or 99. */
    serviceFee: number;
    /** True where the sheet marks the line "(ADH)" (KEY: Accidental Damage from Handling). */
    adh: boolean;
    /** Sheet's narrowing of ADH, e.g. printers "(ADH - Screen Only)" → "screen only". */
    adhQualifier?: string;
    /** Sub-types the sheet lists under this line (TVs → LCD / Plasma / LED). Rendered as pills. */
    alsoIncludes?: string[];
    /** Verbatim caveats from the sheet (exclusions, Non-INS/Non-OEM KEY terms, PC-claim rule). */
    notes?: string[];
}

export interface PlanHighlight {
    label: string;
    /** Bolded value, e.g. "Day 1". */
    value?: string;
    /** PDF field this fact is lifted from. */
    source: string;
}

export interface PlanSectionRow {
    label: string;
    value: string;
}

export interface PlanSection {
    id: string;
    title: string;
    rows: PlanSectionRow[];
    /** PDF field(s) every row in this section is lifted from. */
    source: string;
}

export interface VhdpPlanData {
    meta: { productName: string; referenceLabel: string; version: string; sourceFile: string };
    atAGlance: {
        monthlyPrice: string;
        priceNote: string;
        priceSource: string;
        highlights: PlanHighlight[];
    };
    sections: PlanSection[];
    categories: VhdpCategory[];
    devices: VhdpDevice[];
}

const categories: VhdpCategory[] = [
    { id: "home-entertainment", name: "Home entertainment", source: `${PC} › Home Entertainment` },
    { id: "smart-home", name: "Smart home", source: `${PC} › Smart Home` },
    { id: "smart-home-healthcare", name: "Smart home healthcare", source: `${PC} › Smart Home Healthcare` },
    { id: "smart-pet-tech", name: "Smart pet tech", source: `${PC} › Smart Pet Tech` },
    { id: "smart-floorcare", name: "Smart floorcare", source: `${PC} › Smart Floorcare` },
    { id: "wearables", name: "Wearables", source: `${PC} › Wearables` },
    { id: "home-office", name: "Home office", source: `${PC} › Home Office` },
];

const devices: VhdpDevice[] = [
    // ---- Home Entertainment (11 lines on the sheet) ----
    {
        id: "home-entertainment:tvs",
        name: "TVs",
        categoryId: "home-entertainment",
        serviceFee: 99,
        adh: false,
        alsoIncludes: ["LCD", "Plasma", "LED"], // sheet: "TVs — LCD, Plasma, LED"
    },
    {
        // Sheet has ONE line: "Gaming Systems & Handheld Gaming Devices $99 (ADH)".
        id: "home-entertainment:gaming-systems-handheld",
        name: "Gaming systems & handheld gaming devices",
        categoryId: "home-entertainment",
        serviceFee: 99,
        adh: true,
    },
    {
        id: "home-entertainment:vr-headsets",
        name: "Virtual reality headsets",
        categoryId: "home-entertainment",
        serviceFee: 99,
        adh: true,
    },
    {
        id: "home-entertainment:portable-disc-players",
        name: "Portable DVD/Blu-ray players",
        categoryId: "home-entertainment",
        serviceFee: 0,
        adh: true,
    },
    {
        id: "home-entertainment:gaming-controllers",
        name: "Gaming controllers",
        categoryId: "home-entertainment",
        serviceFee: 0,
        adh: true,
        notes: ["Original and non-OEM controllers."], // sheet: "(Original & NEW Non-OEM)"
    },
    {
        id: "home-entertainment:audio-headsets",
        name: "Audio headsets",
        categoryId: "home-entertainment",
        serviceFee: 49,
        adh: true,
    },
    {
        id: "home-entertainment:home-theater-systems",
        name: "Home theater systems",
        categoryId: "home-entertainment",
        serviceFee: 99,
        adh: false,
        // sheet parenthetical
        notes: ["Receiver, sound bar, speakers, Blu-ray player, amplifier, subwoofer and tuner."],
    },
    {
        id: "home-entertainment:wireless-speakers",
        name: "Bluetooth & Wi-Fi enabled speakers",
        categoryId: "home-entertainment",
        serviceFee: 99,
        adh: false,
    },
    {
        id: "home-entertainment:disc-players",
        name: "DVD & Blu-ray players",
        categoryId: "home-entertainment",
        serviceFee: 0,
        adh: false,
    },
    {
        id: "home-entertainment:streaming-devices",
        name: "Audio/video streaming devices",
        categoryId: "home-entertainment",
        serviceFee: 0,
        adh: true,
        notes: ["Verizon-branded or provided A/V devices are excluded from coverage."], // sheet NOTE
    },
    {
        id: "home-entertainment:original-remotes",
        name: "Original remotes",
        categoryId: "home-entertainment",
        serviceFee: 0,
        adh: false,
    },

    // ---- Smart Home (16) — none marked (ADH) on the sheet ----
    {
        id: "smart-home:smart-door-locks",
        name: "Smart door locks",
        categoryId: "smart-home",
        serviceFee: 49,
        adh: false,
    },
    {
        id: "smart-home:smart-thermostats",
        name: "Smart thermostats",
        categoryId: "smart-home",
        serviceFee: 49,
        adh: false,
    },
    {
        id: "smart-home:smart-security-cameras",
        name: "Smart home security cameras",
        categoryId: "smart-home",
        serviceFee: 49,
        adh: false,
    },
    {
        id: "smart-home:smart-video-doorbells",
        name: "Smart video doorbells",
        categoryId: "smart-home",
        serviceFee: 49,
        adh: false,
    },
    {
        id: "smart-home:smart-video-products",
        name: "Smart video products",
        categoryId: "smart-home",
        serviceFee: 49,
        adh: false,
    },
    {
        id: "smart-home:smart-device-hubs",
        name: "Smart device hubs",
        categoryId: "smart-home",
        serviceFee: 0,
        adh: false,
    },
    {
        id: "smart-home:smart-light-dimmers",
        name: "Smart light dimmers",
        categoryId: "smart-home",
        serviceFee: 0,
        adh: false,
    },
    {
        id: "smart-home:smart-smoke-detectors",
        name: "Smart smoke detectors",
        categoryId: "smart-home",
        serviceFee: 0,
        adh: false,
    },
    {
        id: "smart-home:smart-carbon-monoxide-detectors",
        name: "Smart carbon monoxide detectors",
        categoryId: "smart-home",
        serviceFee: 0,
        adh: false,
    },
    {
        id: "smart-home:smart-alarm-glassbreak-sensors",
        name: "Smart alarm glassbreak sensors",
        categoryId: "smart-home",
        serviceFee: 0,
        adh: false,
    },
    {
        id: "smart-home:smart-alarm-flood-freeze-sensors",
        name: "Smart alarm flood/freeze sensors",
        categoryId: "smart-home",
        serviceFee: 0,
        adh: false,
    },
    {
        id: "smart-home:smart-alarm-keypads",
        name: "Smart alarm keypads",
        categoryId: "smart-home",
        serviceFee: 0,
        adh: false,
    },
    {
        id: "smart-home:smart-alarm-motion-detectors",
        name: "Smart alarm motion detectors",
        categoryId: "smart-home",
        serviceFee: 0,
        adh: false,
    },
    {
        id: "smart-home:smart-alarm-range-extenders",
        name: "Smart alarm range extenders",
        categoryId: "smart-home",
        serviceFee: 0,
        adh: false,
    },
    {
        id: "smart-home:smart-alarm-contact-sensors",
        name: "Smart alarm contact sensors",
        categoryId: "smart-home",
        serviceFee: 0,
        adh: false,
    },
    {
        id: "smart-home:smart-alarm-panic-buttons",
        name: "Smart alarm panic buttons",
        categoryId: "smart-home",
        serviceFee: 0,
        adh: false,
    },

    // ---- Smart Home Healthcare (3) — all $0 (ADH) ----
    {
        id: "smart-home-healthcare:pulse-oximeters",
        name: "Pulse oximeters",
        categoryId: "smart-home-healthcare",
        serviceFee: 0,
        adh: true,
    },
    { id: "smart-home-healthcare:ekgs", name: "EKGs", categoryId: "smart-home-healthcare", serviceFee: 0, adh: true },
    {
        id: "smart-home-healthcare:blood-pressure-monitors",
        name: "Blood pressure monitors",
        categoryId: "smart-home-healthcare",
        serviceFee: 0,
        adh: true,
    },

    // ---- Smart Pet Tech (3) ----
    {
        id: "smart-pet-tech:pet-collars",
        name: "Pet collars",
        categoryId: "smart-pet-tech",
        serviceFee: 99,
        adh: true,
        notes: ["Excludes fence hardware."], // sheet: "Pet Collars (Excluding Fence Hardware)"
    },
    {
        id: "smart-pet-tech:auto-fetch-machines",
        name: "Auto fetch machines",
        categoryId: "smart-pet-tech",
        serviceFee: 49,
        adh: false,
    },
    {
        id: "smart-pet-tech:pet-food-dispensers",
        name: "Pet snack and food dispensers",
        categoryId: "smart-pet-tech",
        serviceFee: 49,
        adh: false,
    },

    // ---- Smart Floorcare (2) ----
    {
        id: "smart-floorcare:robotic-mops",
        name: "Robotic mops",
        categoryId: "smart-floorcare",
        serviceFee: 99,
        adh: false,
    },
    {
        id: "smart-floorcare:robotic-vacuums",
        name: "Robotic vacuums",
        categoryId: "smart-floorcare",
        serviceFee: 99,
        adh: false,
    },

    // ---- Wearables (3) ----
    {
        id: "wearables:augmented-reality-glasses",
        name: "Augmented reality glasses",
        categoryId: "wearables",
        serviceFee: 99,
        adh: true,
    },
    {
        id: "wearables:smartwatches",
        name: "Smartwatches",
        categoryId: "wearables",
        serviceFee: 49,
        adh: true,
        // sheet KEY: "Non-INS – Connected (has MDN) thru Verizon and NOT covered by a Verizon insurance plan."
        notes: ["Non-INS: connected (has MDN) through Verizon and not covered by a Verizon insurance plan."],
    },
    {
        id: "wearables:health-fitness-bands",
        name: "Health and fitness bands",
        categoryId: "wearables",
        serviceFee: 49,
        adh: true,
    },

    // ---- Home Office (11) — items marked * on the sheet must be claimed as part of a PC ----
    {
        id: "home-office:desktop-computers",
        name: "Desktop computers",
        categoryId: "home-office",
        serviceFee: 99,
        adh: false,
    },
    { id: "home-office:laptops", name: "Laptops", categoryId: "home-office", serviceFee: 99, adh: true },
    {
        id: "home-office:tablets",
        name: "Tablets",
        categoryId: "home-office",
        serviceFee: 99,
        adh: true,
        notes: ["Non-INS: connected (has MDN) through Verizon and not covered by a Verizon insurance plan."],
    },
    {
        id: "home-office:printers",
        name: "Printers",
        categoryId: "home-office",
        serviceFee: 99,
        adh: true,
        adhQualifier: "screen only", // sheet: "(ADH - Screen Only)"
    },
    {
        id: "home-office:mesh-home-routers",
        name: "Mesh home routers",
        categoryId: "home-office",
        serviceFee: 99,
        adh: false,
        notes: [
            "Verizon-branded or provided routers are excluded from coverage.", // sheet NOTE
            "Must be claimed as part of a PC (desktop, laptop, or tablet).", // sheet * rule
        ],
    },
    {
        id: "home-office:non-mesh-home-routers",
        name: "Non-mesh home routers",
        categoryId: "home-office",
        serviceFee: 0,
        adh: false,
        notes: ["Verizon-branded or provided routers are excluded from coverage."], // sheet NOTE
    },
    {
        id: "home-office:external-hard-drives",
        name: "External hard drives",
        categoryId: "home-office",
        serviceFee: 0,
        adh: false,
    },
    {
        id: "home-office:keyboard-mouse",
        name: "Keyboard & mouse",
        categoryId: "home-office",
        serviceFee: 0,
        adh: false,
        notes: ["Must be claimed as part of a PC (desktop, laptop, or tablet)."], // sheet *
    },
    {
        id: "home-office:external-monitors",
        name: "External monitors",
        categoryId: "home-office",
        serviceFee: 0,
        adh: false,
        notes: ["Must be claimed as part of a PC (desktop, laptop, or tablet)."], // sheet *
    },
    {
        id: "home-office:modems",
        name: "Modems",
        categoryId: "home-office",
        serviceFee: 0,
        adh: false,
        notes: ["Must be claimed as part of a PC (desktop, laptop, or tablet)."], // sheet *
    },
    {
        id: "home-office:desktop-speaker-sets",
        name: "External desktop speaker sets",
        categoryId: "home-office",
        serviceFee: 0,
        adh: false,
        notes: ["Must be claimed as part of a PC (desktop, laptop, or tablet)."], // sheet *
    },
];

export const vhdpPlanData: VhdpPlanData = {
    meta: {
        productName: "Verizon Home Device Protect",
        referenceLabel: "VHDP reference content",
        version: "V1.0",
        sourceFile: "VDT-20260331-1_VHDP_11x17_Printable_Sheet_260331AP_V1.pdf",
    },
    atAGlance: {
        monthlyPrice: "$25",
        priceNote: "plus applicable tax", // sheet: "$25 plus applicable tax"
        priceSource: `${GI} › Price Per Month`,
        highlights: [
            { label: "Tech support", value: "Day 1", source: `${GI} › Tech Support Start` },
            { label: "Coverage starts", value: "Day 31", source: `${GI} › Product Coverage Start` },
            { label: "Service fees", value: "$0, $49, or $99 per approved claim", source: `${GI} › Service Fees` },
            // One bullet, not two, for sales compliance.
            {
                label: "Claim limits:",
                value: "$3,000 per claim and $5,000 total per 12 month period",
                source: `${GI} › Claim Limit`,
            },
        ],
    },
    sections: [
        {
            id: "price-billing",
            title: "Price, billing & cancellation",
            source: `${GI} › Price Per Month / Billed To / Billing Start / Renewal / Cancellation`,
            rows: [
                { label: "Price", value: "$25/month plus applicable tax." },
                { label: "Billed to", value: "Verizon account." },
                {
                    label: "Billing starts",
                    value: "Upon enrollment. First month is prorated based on the enrollment day in the calendar month.",
                },
                { label: "Renewal", value: "Automatically renews month to month until canceled." },
                {
                    label: "Cancellation",
                    value: "Anytime, no fee, prorated refund. Full refund if canceled within the first 30 days.",
                },
            ],
        },
        {
            id: "coverage-eligibility",
            title: "Coverage & eligibility",
            source: `${GI} › Covered Locations / Covered Products / Tech Support Start / Product Coverage Start; ${PC} networking NOTE`,
            rows: [
                {
                    label: "Covered locations",
                    value: "Verizon primary address plus a qualifying 2nd address. The 2nd address can be changed up to 3 times per 12-month period and must be primarily residential, not business.",
                },
                { label: "Covered products", value: "Existing eligible products plus future eligible purchases." },
                {
                    label: "Tech support starts",
                    value: "Day 1, including In-Store Complete Tech Care, In-Home Tech Installs, and In-Home Visits.",
                },
                { label: "Product coverage starts", value: "Day 31." },
                {
                    label: "Networking",
                    value: "Smart home, smart home healthcare, smart pet tech, and smart floorcare products must use Wi-Fi, Bluetooth, ZigBee, Z-Wave, Insteon, Thread, or another approved networking solution to be eligible.",
                },
            ],
        },
        {
            id: "claims-repair",
            title: "Claims, repair & replacement",
            source: `${GI} › Service Fees / # of Claims / Claim Limit / Repair Options`,
            rows: [
                { label: "Service fees", value: "$0, $49, or $99 per approved claim." },
                { label: "Number of claims", value: "Unlimited, up to the annual aggregate limit." },
                {
                    label: "Claim limit",
                    value: "$3,000 maximum per claim and $5,000 aggregate per 12-month rolling calendar for eligible tech (aggregate is shared with the 2nd address).",
                },
                {
                    label: "Repair options",
                    value: "Serviced by local uBreakiFix stores or mailed to our repair depot. If we can't repair it, we'll replace it or reimburse the customer.",
                },
            ],
        },
        {
            id: "support-services",
            title: "Support & services",
            source: `${GI} › Tech Support / In-Store Services / In-Home Expert Support Visits / In-Home Tech Installs / App`,
            rows: [
                { label: "Tech support", value: "24/7." },
                {
                    label: "In-store services",
                    value: "$0 Complete Tech Care for laptops, tablets, and gaming systems: device tune-up, cleanings, virus removal (laptops only), and recycling of old portable home tech.",
                },
                {
                    label: "In-home expert visits",
                    value: "Two visits per 12-month period (shared between locations if the customer has a 2nd location).",
                },
                {
                    label: "In-home tech installs",
                    value: "$0 on eligible tech for Verizon Home Internet customers (wireless & Fios); $49 per eligible item otherwise.",
                },
                { label: "App", value: "Tech Coach." },
            ],
        },
    ],
    categories,
    devices,
};
